import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const ACTION_LABELS = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  shipped: 'Shipped',
  bill_uploaded: 'Bill Uploaded',
  bill_deleted: 'Bill Removed',
};

const ACTION_COLORS = {
  create: '#2e7d32',
  update: '#ed6c02',
  delete: '#d32f2f',
  shipped: '#1565c0',
  bill_uploaded: '#6a1b9a',
  bill_deleted: '#c2185b',
};

const ENTITY_LABELS = {
  pallet: 'Pallet',
  wheel: 'Wheel',
  shipment: 'Shipment',
};

const PAGE_SIZE = 20;

const SettingsAuditTrail = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (filterAction) params.action = filterAction;
      if (filterEntity) params.entityType = filterEntity;
      if (search.trim()) params.search = search.trim();

      const res = await api.get('/audit', { params });
      setLogs(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch {
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, filterAction, filterEntity, search]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const formatTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const parseDetails = (details) => {
    if (!details) return null;
    if (typeof details === 'string') {
      try { return JSON.parse(details); } catch { return null; }
    }
    return details;
  };

  const renderDetails = (det) => {
    if (!det) return null;
    const parts = [];
    if (det.customer) parts.push(`Customer: ${det.customer}`);
    if (det.pallet_count != null) parts.push(`Pallets: ${det.pallet_count}`);
    else if (det.pallets != null) parts.push(`Pallets: ${det.pallets}`);
    if (det.item_count != null) parts.push(`Items: ${det.item_count}`);
    else if (det.items != null) parts.push(`Items: ${det.items}`);
    if (det.weight != null) parts.push(`Weight: ${Number(det.weight).toFixed(1)} kg`);
    if (det.status) parts.push(`Status: ${det.status}`);
    return parts.map((p, i) => <span key={i}>{p}</span>);
  };

  const hasExpandable = (det) => {
    return det && (det.item_list || (det.old_items && det.new_items) || det.pallet_list);
  };

  const renderExpandedDetails = (det) => {
    if (!det) return null;
    return (
      <div className="audit-expanded">
        {det.item_list && (
          <div className="audit-items-section">
            <strong>Items:</strong>
            <table className="audit-items-table">
              <thead><tr><th>Name</th><th>Qty</th><th>Lot</th><th>Weight</th></tr></thead>
              <tbody>
                {det.item_list.map((item, i) => (
                  <tr key={i}><td>{item.name}</td><td>{item.qty}</td><td>{item.lot || '\u2014'}</td><td>{Number(item.weight || 0).toFixed(1)} kg</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {det.old_items && det.new_items && (
          <div className="audit-diff">
            <div className="audit-diff-col">
              <strong>Before:</strong>
              <ul>{det.old_items.map((item, i) => <li key={i}>{item.name} x {item.qty} (Lot: {item.lot || '\u2014'}, {Number(item.weight || 0).toFixed(1)} kg)</li>)}</ul>
            </div>
            <div className="audit-diff-col">
              <strong>After:</strong>
              <ul>{det.new_items.map((item, i) => <li key={i}>{item.name} x {item.qty} (Lot: {item.lot || '\u2014'}, {Number(item.weight || 0).toFixed(1)} kg)</li>)}</ul>
            </div>
          </div>
        )}
        {det.pallet_list && (
          <div className="audit-items-section">
            <strong>Pallets:</strong>
            <table className="audit-items-table">
              <thead><tr><th>Pallet</th><th>Weight</th><th>Verified By</th></tr></thead>
              <tbody>
                {det.pallet_list.map((p, i) => (
                  <tr key={i}><td>{p.number}</td><td>{Number(p.weight || 0).toFixed(1)} kg</td><td>{p.verified_by || '\u2014'}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="audit-trail">
      <div className="audit-header">
        <h3 className="audit-title">Audit Trail</h3>
        <div className="audit-filters">
          <input
            className="filter-select audit-search-input"
            type="text"
            placeholder="Search by pallet, shipment, user..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
          />
          <select
            className="filter-select"
            value={filterEntity}
            onChange={e => { setFilterEntity(e.target.value); setPage(0); }}
          >
            <option value="">All Types</option>
            <option value="pallet">Pallets</option>
            <option value="wheel">Wheels</option>
            <option value="shipment">Shipments</option>
          </select>
          <select
            className="filter-select"
            value={filterAction}
            onChange={e => { setFilterAction(e.target.value); setPage(0); }}
          >
            <option value="">All Actions</option>
            <option value="create">Created</option>
            <option value="update">Updated</option>
            <option value="delete">Deleted</option>
            <option value="shipped">Shipped</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="audit-loading">Loading audit logs...</div>
      ) : logs.length === 0 ? (
        <div className="audit-empty">No audit logs found.</div>
      ) : (
        <>
          <div className="audit-table-wrap">
            <table className="audit-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Type</th>
                  <th>Reference</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const det = parseDetails(log.details);
                  const expandable = hasExpandable(det);
                  const isExpanded = expandedId === log.id;
                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        className={expandable ? 'audit-row-expandable' : ''}
                        onClick={() => expandable && setExpandedId(isExpanded ? null : log.id)}
                      >
                        <td className="audit-time">{formatTime(log.created_at)}</td>
                        <td className="audit-user">{log.user_name}</td>
                        <td>
                          <span
                            className="audit-action-badge"
                            style={{ background: ACTION_COLORS[log.action] || '#666' }}
                          >
                            {ACTION_LABELS[log.action] || log.action}
                          </span>
                        </td>
                        <td className="audit-type">{ENTITY_LABELS[log.entity_type] || log.entity_type}</td>
                        <td className="audit-entity">{log.entity_label || `#${log.entity_id}`}</td>
                        <td className="audit-details">
                          {renderDetails(det)}
                          {expandable && <span className="audit-expand-icon">{isExpanded ? '\u25B2' : '\u25BC'}</span>}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="audit-expanded-row">
                          <td colSpan="6">{renderExpandedDetails(det)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="audit-pagination">
              <button className="btn-secondary" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                Previous
              </button>
              <span className="audit-page-info">Page {page + 1} of {totalPages} ({total} entries)</span>
              <button className="btn-secondary" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SettingsAuditTrail;
